/**
 * `loading_screen` renderer — composes `@nextday/funnel-ui` `LoadingScreen`.
 * The screen counts up through `steps` and auto-advances when `totalMs`
 * elapses (via `onComplete`). A "Skip the wait" button appears after
 * `showSkipAfterMs` and also advances the funnel.
 *
 * Step JSON shape (plan §5 step 5 — pre-account loading beat):
 *
 *   {
 *     "type": "loading_screen",
 *     "data": {
 *       "headline": "Tuning your match...",
 *       "totalMs": 12000,
 *       "showSkipAfterMs": 6000,
 *       "skippable": true,
 *       "steps": [
 *         { "label": "Reading your answers" },
 *         { "label": "Matching personality traits" },
 *         { "label": "Finding ideal characters" },
 *         { "label": "Tuning the conversation style" }
 *       ],
 *       "testimonials": []
 *     }
 *   }
 */
import { LoadingScreen } from "@nextday/funnel-ui";
import { z } from "zod";

import { createRenderer } from "@survey-kit/registry";
import { testimonialSchema } from "@survey-kit/renderers/testimonialSchema";
import type { RendererProps } from "@survey-kit/types";

const loadingStepSchema = z.object({
  label: z.string().min(1),
  durationMs: z.number().int().positive().optional(),
});

export const loadingScreenSchema = z
  .object({
    headline: z.string().optional(),
    steps: z.array(loadingStepSchema).optional(),
    totalMs: z.number().int().positive().optional(),
    showSkipAfterMs: z.number().int().nonnegative().optional(),
    /**
     * When true, the renderer wires onSkip to onAdvance so the user can
     * bail out early. Default false (no skip button).
     */
    skippable: z.boolean().optional(),
    testimonials: z.array(testimonialSchema).optional(),
  })
  .strict();

export type LoadingScreenData = z.infer<typeof loadingScreenSchema>;

const LoadingScreenRenderer = ({ data, step, onAdvance }: RendererProps<LoadingScreenData>) => (
  <LoadingScreen
    headline={data.headline}
    steps={data.steps}
    totalMs={data.totalMs}
    showSkipAfterMs={data.showSkipAfterMs}
    testimonials={data.testimonials}
    onComplete={() => onAdvance()}
    onSkip={data.skippable ? () => onAdvance() : undefined}
    testId={`renderer-loading-screen-${step.id}`}
  />
);

export const loadingScreenRenderer = createRenderer({
  type: "loading_screen",
  dataSchema: loadingScreenSchema,
  label: "Loading screen",
  Component: LoadingScreenRenderer,
});

export default loadingScreenRenderer;
